import { useTranslation } from 'react-i18next';
import redArrow from '../assets/images/red-arrow.png';
import EarResults from '../components/ear-results';
import constants from '../constants';
import { EarResult } from '../types';
import {
  classifyHearingText,
  colorCodes,
  hmacEncode,
  sanitizePayload,
  TColorCodes,
} from '../utils';
import { useLogError } from '../hooks/useLogError';
import { loadDetailedResults } from '../utils/detailedResultsStorage';

export type Side = 'Left' | 'Right' | 'Oído izquierdo' | 'Oído derecho';

interface URLResultData {
  overall: number;
  L: EarResult;
  R: EarResult;
}

const DetailedResults = () => {
  const { t, i18n } = useTranslation('detailedResults');
  const currentLanguage = i18n.language;

  const { logError } = useLogError();
  const stored = loadDetailedResults();
  const date = new Date(stored?.date || Date.now());
  const dateFormatted = date.toLocaleDateString('en', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  const name = stored?.name || '';
  const results: URLResultData | undefined = stored?.results;
  const hideButtons = stored?.hideButtons;
  const contactId = stored?.contactid;

  const mapIndicatorPosition = (overallValue: number) => {
    if (overallValue <= 25) return 0;
    if (overallValue <= 40) return 25;
    if (overallValue <= 70) return 50;
    return 75;
  };

  const callAndPostData = async () => {
    const contactData = {
      id: contactId,
    };

    // initialize the activityData
    const data = {
      contact: contactData,
      activitytypeid: 'JHT Web Results Call',
    };

    const sanitizedData = sanitizePayload(data);
    if (!sanitizedData) {
      return;
    }

    const activityData = JSON.stringify(sanitizedData);
    const signature = await hmacEncode(activityData, constants['hmacSecret']);

    try {
      const response = await fetch(constants['apiBaseUrl'], {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-AUD-Signature': signature,
        },
        body: activityData,
      });

      if (!response?.ok) {
        throw new Error(
          `Failed to POST to ${constants['apiBaseUrl']} on ${data.activitytypeid} for contact id: ${contactData.id}`,
        );
      }

      // Handle successful response here
    } catch (error) {
      logError(error);
    }
  };

  if (!results) {
    return (
      <div className="detailed-results">
        <h1>{t('your-results-suggest')}</h1>
        <p>
          Your results are no longer available in this browser session. Check
          the email we sent you, or retake the test.
        </p>
      </div>
    );
  }

  return (
    <div className="detailed-results">
      <p className="name-date">Prepared for {name + ' on ' + dateFormatted}.</p>
      <h1>{t('your-results-suggest')}</h1>
      <h2>{classifyHearingText(results.overall, currentLanguage)}</h2>

      <div className="loss-indicator loss-indicator-position">
        <div
          style={{ marginRight: `${mapIndicatorPosition(results.overall)}%` }}
        >
          <p>{t('your-overall-hearing')}</p>
          <img src={redArrow} alt="red arrow" />
        </div>
      </div>

      <div className="top-severity-bar">
        {Object.keys(colorCodes).map((code, i) => (
          <div className="bar-section" key={`bar-${i}`}>
            <div
              style={{ background: `${colorCodes[code as keyof TColorCodes]}` }}
            />
            <p>{t(`difficulty.${code}`)}</p>
          </div>
        ))}
      </div>

      <EarResults side={t('left-ear') as Side} data={results.L} />
      <EarResults side={t('right-ear') as Side} data={results.R} />

      {!hideButtons ? (
        <div className="bottom-buttons">
          <a
            className="shop-button"
            href="https://www.audicus.com/best-hearing-aids"
          >
            Shop Hearing Aids
          </a>
          <a
            className="call-button"
            href="tel:888-203-1096,"
            onClick={callAndPostData}
            style={{ background: '#b55821' }}
          >
            Call Audicus Specialist
          </a>
        </div>
      ) : null}

      <div className="bottom-info">
        <p>
          {t('feedback-message')}{' '}
          <a
            style={{ color: 'black', fontFamily: 'Noto Sans Bold' }}
            href="mailto:research@audicus.com"
          >
            {t('email-research')}
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default DetailedResults;
